import type { FrameData } from "@/types";
import { Quaternion, Vector3 } from "three";

export type AnimatedFrameData = FrameData & {
  rotation: [number, number, number, number];
};

export type AnimatedFrameController = {
  completed: boolean;
  currentFrameTick: number;
  frameData: AnimatedFrameData[];
  frameIndex: number;
  lastFrameTimeMs: number;
  nextPosition: Vector3;
  nextQuaternion: Quaternion;
  previousPosition: Vector3;
  previousQuaternion: Quaternion;
  started: boolean;
};

export const animatedFrameIntervalMs = 1000 / 15;

export const isAnimatedFrameData = (
  frame: FrameData | undefined,
): frame is AnimatedFrameData => Boolean(frame?.rotation);

export const getAnimatedFrameData = (frameData: FrameData[] = []) =>
  frameData.filter(isAnimatedFrameData);

export const createAnimatedFrameController = (
  frameData: AnimatedFrameData[],
): AnimatedFrameController => {
  const firstFrame = frameData[0];
  const firstPosition = new Vector3(...(firstFrame?.position ?? [0, 0, 0]));
  const firstQuaternion = new Quaternion(
    ...(firstFrame?.rotation ?? [0, 0, 0, 1]),
  );

  return {
    completed: false,
    currentFrameTick: 0,
    frameData,
    frameIndex: 0,
    lastFrameTimeMs: 0,
    nextPosition: firstPosition.clone(),
    nextQuaternion: firstQuaternion.clone(),
    previousPosition: firstPosition.clone(),
    previousQuaternion: firstQuaternion.clone(),
    started: false,
  };
};

export const recordAnimatedFrameTick = (
  controller: AnimatedFrameController,
) => {
  controller.currentFrameTick += 1;

  return controller.currentFrameTick;
};

export const startAnimatedFrameController = (
  controller: AnimatedFrameController,
  lastFrameTimeMs = controller.lastFrameTimeMs,
) => {
  if (controller.started) return false;

  controller.started = true;
  controller.lastFrameTimeMs = lastFrameTimeMs;

  return true;
};

export const advanceAnimatedFrameController = (
  controller: AnimatedFrameController,
  nowMs: number,
) => {
  if (
    controller.completed ||
    !controller.frameData.length ||
    nowMs - controller.lastFrameTimeMs < animatedFrameIntervalMs
  ) {
    return { advanced: false, completed: false };
  }

  controller.lastFrameTimeMs = nowMs;

  if (controller.frameIndex === controller.frameData.length - 1) {
    controller.completed = true;

    return { advanced: false, completed: true };
  }

  const previousFrame = controller.frameData[controller.frameIndex];
  controller.frameIndex += 1;

  const nextFrame = controller.frameData[controller.frameIndex];

  controller.previousPosition.set(...previousFrame.position);
  controller.previousQuaternion.set(...previousFrame.rotation);
  controller.nextPosition.set(...nextFrame.position);
  controller.nextQuaternion.set(...nextFrame.rotation);

  return { advanced: true, completed: false };
};

export const getAnimatedFrameProgress = (
  controller: AnimatedFrameController,
  nowMs: number,
) =>
  controller.completed
    ? 1
    : Math.min(
        1,
        (nowMs - controller.lastFrameTimeMs) / animatedFrameIntervalMs,
      );

export const writeAnimatedFrameTransform = ({
  controller,
  nowMs,
  offsetQuaternion,
  targetPosition,
  targetQuaternion,
}: {
  controller: AnimatedFrameController;
  nowMs: number;
  offsetQuaternion?: Quaternion;
  targetPosition: Vector3;
  targetQuaternion: Quaternion;
}) => {
  const frameProgress = getAnimatedFrameProgress(controller, nowMs);

  targetPosition
    .copy(controller.previousPosition)
    .lerp(controller.nextPosition, frameProgress);
  targetQuaternion
    .copy(controller.previousQuaternion)
    .slerp(controller.nextQuaternion, frameProgress);

  if (offsetQuaternion) {
    targetQuaternion.multiply(offsetQuaternion);
  }
};
